import 'react-tabs/style/react-tabs.css'
import 'leaflet/dist/leaflet.css'

import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import L from 'leaflet'
import { uniq } from 'lodash'
import React, { memo, useEffect, useState } from 'react'
import LazyLoad from 'react-lazyload'
import { MapContainer, Marker, TileLayer } from 'react-leaflet'
import Fade from 'react-reveal/Fade'

import { Poi, Props as PoiProps } from './Poi'

export interface Props {
  address?: string
  directions?: { [key: string]: any }
  googleMapsURL?: string
  image?: string
  languageCode: string
  logo?: string
  longitude?: string
  latitude?: string
  poiList?: PoiProps[]
  siteName?: string
  title?: string
  zoom?: string
}

export const Directions = memo(function Directions({
  address,
  googleMapsURL,
  image,
  logo,
  languageCode,
  longitude,
  latitude,
  poiList,
  siteName,
  zoom,
}: Props) {
  const [markerIcon, setMarkerIcon] = useState(L.divIcon)

  const [modalOpen, setModal] = useState(null)
  const openModal = (e: any) => {
    const {
      target: {
        dataset: { modal },
      },
    } = e

    if (modal) setModal(modal)
  }

  useEffect(() => {
    setMarkerIcon(
      L.divIcon({
        iconSize: [55, 80],
        iconAnchor: [27, 80],
        html: `<img src="${logo}" alt="${siteName}" width="35" height="23" /><svg xmlns="http://www.w3.org/2000/svg" width="55.007" height="80" viewBox="0 0 55.007 80"><path d="M383.218,354.361a27.532,27.532,0,0,0-27.505,27.5v.1a30.507,30.507,0,0,0,4.216,15.467L381,433.2a2.347,2.347,0,0,0,2.016,1.157h.008a2.346,2.346,0,0,0,2.016-1.143l21.36-35.711a30.5,30.5,0,0,0,4.323-15.65A27.533,27.533,0,0,0,383.218,354.361Z" transform="translate(-355.713 -354.361)"/></svg>`,
        className: 'main-icon',
      }),
    )
  }, [])

  return (
    <Container>
      <Fade bottom distance="3.75rem">
        <>
          <Head>
            <Inner>
              <Label>{siteName}</Label>
              {address ? <Address>{address}</Address> : null}
            </Inner>
          </Head>
          <Center>
            <Wrapper row wrap stretch>
              <LeftSide>
                {image ? (
                  <LazyLoad>
                    <Image style={{ backgroundImage: `url(${image})` }} />
                  </LazyLoad>
                ) : null}
                {poiList ? (
                  <PoiList>
                    {uniq(poiList).map((item, index) => (
                      <Poi
                        className={
                          modalOpen === `${index}` ? 'active' : undefined
                        }
                        dataModal={index}
                        key={index}
                        {...item}
                        onClick={(e: any) => {
                          openModal(e)
                        }}
                      />
                    ))}
                  </PoiList>
                ) : null}
              </LeftSide>
              <Map>
                {typeof window !== undefined && latitude && longitude ? (
                  <MapContainer
                    center={[Number(latitude), Number(longitude)]}
                    dragging={L.Browser && L.Browser.mobile ? false : true}
                    zoom={Number(zoom)}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    />
                    <Marker
                      icon={markerIcon}
                      position={[Number(latitude), Number(longitude)]}
                    />
                    {poiList
                      ? uniq(poiList).map((item, index) => (
                          <Marker
                            key={index}
                            icon={
                              typeof window !== 'undefined'
                                ? L.divIcon({
                                    iconSize: [24, 36],
                                    iconAnchor: [12, 36],
                                    html: '<svg xmlns="http://www.w3.org/2000/svg" width="24.749" height="36" viewBox="0 0 24.749 36"><path d="M92.379,0A12.389,12.389,0,0,0,80,12.375v.047a13.729,13.729,0,0,0,1.9,6.961l9.478,16.1a1.057,1.057,0,0,0,.907.521h0a1.057,1.057,0,0,0,.907-.514l9.611-16.069a13.722,13.722,0,0,0,1.945-7.042A12.389,12.389,0,0,0,92.379,0ZM92.29,17.922A5.319,5.319,0,1,1,97.609,12.6a5.319,5.319,0,0,1-5.319,5.319Z" transform="translate(-80.004)"/></svg>',
                                    className:
                                      modalOpen === `${index}`
                                        ? 'active'
                                        : undefined,
                                  })
                                : undefined
                            }
                            position={[
                              Number(item.latitude),
                              Number(item.longitude),
                            ]}
                          />
                        ))
                      : null}
                  </MapContainer>
                ) : null}
              </Map>
              {googleMapsURL ? (
                <GoogleMapsCTA
                  variant="corners"
                  label={useVocabularyData('open-map', languageCode)}
                  URL={googleMapsURL}
                  rel="noreferrer"
                  target="_blank"
                />
              ) : null}
            </Wrapper>
          </Center>
        </>
      </Fade>
    </Container>
  )
})

const Container = styled.section`
  margin: calc(6rem + 50px) auto 6rem;

  @media (max-width: 1023px) {
    margin: 3.75rem auto;
  }
`

const Head = styled.div`
  max-width: 77rem;
  width: 77vw;
  border: 2px solid ${({ theme }) => theme.colors.variants.neutralLight3};
  border-bottom: 0;
  margin: auto;
  padding: 0 1.5rem;
  text-align: center;

  @media (max-width: 1023px) {
    max-width: 80%;
    padding: 2.25rem 1.5rem;
    max-width: none;
    border: 0;
    padding: 0;
  }
`
const Inner = styled.div`
  position: relative;
  top: -50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 650px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.colors.variants.neutralLight4};

  @media (max-width: 1023px) {
    top: unset;
  }
`

const Label = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-weight: 500;
  font-size: 2.4375rem;
`

const Address = styled.div`
  margin-top: 1.875rem;
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-weight: 600;
  font-size: 0.9375rem;
  text-transform: uppercase;
`

const Center = styled.div`
  position: relative;

  @media (max-width: 1023px) {
    margin-top: 3rem;
  }
`

const Wrapper = styled(FlexBox)`
  margin: auto;
`

const LeftSide = styled.div`
  width: 42%;
  padding: 3rem;
  position: relative;

  @media (max-width: 1023px) {
    display: none;
  }
`

const Image = styled.span`
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    opacity: 0.3;
    background: #53a78b;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
  }
`

const PoiList = styled.div`
  overflow-y: auto;
  background: #53a78b;
  padding: 3.75rem;
  position: relative;
  z-index: 2;
`

const Map = styled.div`
  width: 58%;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  position: relative;

  @media (max-width: 1023px) {
    width: 100%;
    height: 60vh;
    max-height: 37.5rem;

    .leaflet-left {
      display: none;
    }
  }

  @media (max-width: 767px) {
    max-height: 30rem;
  }

  .leaflet-container {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
  .leaflet-marker-icon {
    background: none;
    border: 0;

    @media (max-width: 1023px) {
      display: none;
    }

    &.active,
    &.main-icon {
      svg {
        fill: #53a78b;
      }
    }
    &.active {
      svg {
        animation: bounce 2s 1;
      }
    }
    &.main-icon {
      @media (max-width: 1023px) {
        display: block;
      }
    }
    img {
      position: absolute;
      top: 18px;
      left: 11px;
    }
    svg {
      fill: ${({ theme }) => theme.colors.variants.neutralDark4};
    }
  }
  .leaflet-left {
    top: auto;
    bottom: 0;
    .leaflet-control {
      border: 0;
      border-radius: 0;
      margin: 0;
      a {
        width: 3rem;
        height: 3rem;
        line-height: 2.875rem;
        background: ${({ theme }) => theme.colors.variants.neutralLight4};
        color: ${({ theme }) => theme.colors.variants.neutralDark1};
        font-family: ${({ theme }) => theme.fontFamily.paragraph};
        font-weight: 100;
        font-size: 2.375rem;
        border: 0;
        border-radius: 0;
        transition: 0.3s ease-out;
        &.leaflet-control-zoom-in {
          order: 2;
        }
        &.leaflet-disabled {
          opacity: 0.6;
          pointer-events: none;
        }
        &:hover {
          background: ${({ theme }) => theme.colors.variants.neutralLight3};
        }
      }
    }
  }
  .leaflet-bottom {
    display: none;
  }
  .leaflet-popup {
    .leaflet-popup-content-wrapper {
      background: ${({ theme }) => theme.colors.variants.primaryDark1};
      box-shadow: none;
      border-radius: 0;
      color: ${({ theme }) => theme.colors.variants.neutralLight4};
      font-family: ${({ theme }) => theme.fontFamily.heading};
      font-size: 1rem;
      padding: 1rem;
    }
    .leaflet-popup-content {
      line-height: 1.5rem;
      margin: 0;
    }
    .leaflet-popup-tip {
      background: ${({ theme }) => theme.colors.variants.primaryDark1};
      box-shadow: none;
    }
    .leaflet-popup-close-button {
      display: none;
    }
  }

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-1.875rem);
    }
    60% {
      transform: translateY(-0.9375rem);
    }
  }
`

const GoogleMapsCTA = styled(Button)`
  margin-left: auto;
  margin-right: auto;
`
